import React, { useState } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"

import { editProfile } from "../../store/auth/profile/actions"
import {
  ONBOARDING_ROUTE,
  isOnboardingMandatory,
  onboardingStepOf,
  shouldInviteToSectorMap,
  withOnboardingComplete,
  withSectorMapInviteDismissed,
  withVoluntaryOnboarding,
} from "../../helpers/onboarding"
import OnboardingIntroModal from "./OnboardingIntroModal"
import SectorMapInviteModal from "./SectorMapInviteModal"

/**
 * Decides which onboarding prompt, if any, belongs on screen. Mounted once in
 * the layout so it covers every protected page without either modal having to
 * live inside Designer.js.
 */
const OnboardingHost = ({ profile, onUpdateProfile }) => {
  const history = useHistory()
  const location = useLocation()

  // Closing a prompt without a decision should last the session but not be
  // written to the profile, so it is kept here rather than on the document.
  const [closedThisSession, setClosedThisSession] = useState(false)

  if (!profile || typeof profile !== "object") return null

  const step = onboardingStepOf(profile)
  const onDesigner = location.pathname.indexOf(ONBOARDING_ROUTE) === 0

  // New user, address collected, now being walked through. Existing users who
  // opt in have a completedAt and so never see this wording.
  const showIntro =
    !closedThisSession &&
    onDesigner &&
    step === "site" &&
    isOnboardingMandatory(profile)

  const showInvite =
    !closedThisSession &&
    location.pathname.indexOf("/home") === 0 &&
    shouldInviteToSectorMap(profile)

  if (showIntro) {
    return (
      <OnboardingIntroModal
        isOpen
        onStart={() => setClosedThisSession(true)}
        onSkip={() => {
          // Marks the run finished so the gate releases. Temporary: once the
          // wizard steps exist, finishing should be what releases it.
          setClosedThisSession(true)
          onUpdateProfile(
            withSectorMapInviteDismissed(withOnboardingComplete(profile))
          )
        }}
      />
    )
  }

  if (showInvite) {
    return (
      <SectorMapInviteModal
        isOpen
        onStart={() => {
          setClosedThisSession(true)
          onUpdateProfile(withVoluntaryOnboarding(profile, "site"))
          history.push(ONBOARDING_ROUTE)
        }}
        onDecline={() => {
          setClosedThisSession(true)
          onUpdateProfile(withSectorMapInviteDismissed(profile))
        }}
      />
    )
  }

  return null
}

OnboardingHost.propTypes = {
  profile: PropTypes.any,
  onUpdateProfile: PropTypes.func,
}

const mapStateToProps = state => ({
  profile: state.Profile?.user || null,
})

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: profile => dispatch(editProfile(profile)),
})

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingHost)
